import { defineStore } from 'pinia';
import { io } from 'socket.io-client';
import { computed, ref, watch } from 'vue';

import { env } from '@/env';
import { USER } from '@/models/auth/permissions.enum';
import { useAuthStore } from '@/stores/auth';
import { useImpersonatorStore } from '@/stores/impersonator';
import { useTokenStore } from '@/stores/token';
import { logger } from '@/utils/logger';

const SOCKETIO_EVENT = {
  CONNECT: 'connect',
  RECONNECT: 'reconnect',
};

export const EVENT = {
  REGISTER: 'register',
  REGISTER_USER: 'register_user',
  JOIN_BRAND_ROOMS: 'join_brand_rooms',
  NOTIFICATION: 'notification',
  PDF_GENERATED: 'pdf_generated',
  CSV_GENERATED: 'csv_generated',
  INTERACTIONS_CSV_GENERATED: 'INTERACTIONS_CSV_GENERATED',
  XLSX_GENERATED: 'xlsx_generated',
  SCHEDULED_POST_CALLBACK: 'scheduled_post_callback',
  SCHEDULED_POST_UPDATED: 'scheduled_post_updated',
  SCHEDULER_SETTINGS_UPDATED: 'scheduler_settings_updated',
  CLUSTER_TASK_NOTIFICATION: 'CLUSTER_TASK_NOTIFICATION',
  MEDIA_BULK_DOWNLOAD_GENERATED: 'media_bulk_download_generated',
  MEDIA_BULK_DOWNLOAD_FAILED: 'media_bulk_download_failed',
  MEDIA_MUTED: 'media_muted',
  MEDIA_MUTE_FAILED: 'media_mute_failed',
  MEDIA_MUTE_IN_PROGRESS: 'media_mute_in_progress',
  SONG_ID_COMPLETED: 'song_id_completed',
  PRODUCT_BULK_IMPORT_COMPLETED: 'product_bulk_import_completed',
  PRODUCT_BULK_IMPORT_FAILED: 'product_bulk_import_failed',
  COMPETITOR_RESOLUTION_UPDATED: 'tiktok_competitor_resolution_updated',
};

export const useSocketStore = defineStore('socket', () => {
  const authStore = useAuthStore();
  const tokenStore = useTokenStore();
  const impersonatorStore = useImpersonatorStore();

  let socket = null;

  // Kept independently of the socket: `init()` replaces it, and socket.io listeners belong to the
  // instance they were attached to.
  const listeners = new Map();

  // One-shot waits still pending. Unlike `listeners` these are NOT replayed onto a replacement
  // socket: it lands in different rooms, under a different identity when impersonating, so the
  // awaited event can never arrive and a replay would hang the caller instead of settling it.
  const pendingOnceWaits = new Set();

  // The impersonation header the socket must be built with — stringified so a stored id that changes
  // representation (number vs the string sessionStorage hands back) isn't seen as a new identity.
  const onBehalfOf = computed(() =>
    impersonatorStore.isImpersonating ? String(impersonatorStore.impersonatedUserId) : null,
  );
  const id = ref();

  // Payload hand-offs for two domains that read them through this store. Plain refs, so they cost
  // the transport no imports — the handlers that write them live in `app/socket-listeners.js`.
  // Rehoming them onto their own stores means touching an Options-API string watcher, so it is a
  // separate change.
  const productBulkImportResult = ref(null);
  const schedulerCallbackData = ref(null);

  function registerIdentity() {
    const isLoggedIn = authStore.isLoggedIn;
    const hasToken = !!tokenStore.token;
    const notImpersonating = !impersonatorStore.isImpersonating;

    if (isLoggedIn && hasToken && socket?.connected) {
      socket.emit(EVENT.REGISTER, tokenStore.token);

      const canAccessCommunity = authStore.guard(USER.COMMUNITY.CAN_ACCESS_COMMUNITY);
      const canAccessScheduler = authStore.guard(USER.SCHEDULER.CAN_ACCESS_SCHEDULER);
      const canAccessCreators = authStore.guard(USER.CREATORS.CAN_ACCESS_CREATORS);
      if (notImpersonating && (canAccessScheduler || canAccessCommunity || canAccessCreators)) {
        socket.emit(EVENT.JOIN_BRAND_ROOMS, tokenStore.token);
      }
    }
  }

  function abandonPendingOnceWaits() {
    if (pendingOnceWaits.size === 0) return;

    const abandoned = [...pendingOnceWaits];
    pendingOnceWaits.clear();
    logger.warn('socket dropped while one-shot waits were pending', { count: abandoned.length });

    for (const { onAbandoned } of abandoned) {
      try {
        onAbandoned?.();
      } catch (error) {
        logger.error('socket one-shot wait failed to abandon cleanly', {}, error);
      }
    }
  }

  function disconnect() {
    if (socket) {
      socket.disconnect();
      socket = null;
    }
    abandonPendingOnceWaits();
  }

  function init() {
    if (socket) {
      disconnect();
    }

    const extraHeaders = {};
    if (onBehalfOf.value) {
      extraHeaders['X-On-Behalf-Of'] = onBehalfOf.value;
    }

    socket = io(env.notificationServiceUrl, { extraHeaders });
    socket.on(SOCKETIO_EVENT.CONNECT, () => {
      id.value = socket.id;
      registerIdentity();
    });
    socket.io.on(SOCKETIO_EVENT.RECONNECT, () => {
      id.value = socket.id;
      registerIdentity();
    });
    socket.on('disconnect', (reason, details) => {
      logger.debug(`socket io disconnected, reason: ${reason}`);
      if (details) {
        logger.debug(
          `socket io disconnected, details:
          message=${details.message}
          description=${details.description}
          context=${details.context}`,
        );
      }
      if (reason === 'io server disconnect') {
        logger.warn('socket io server disconnected. Trying to reconnect.');
        // the disconnection was initiated by the server, reconnect manually
        socket.connect();
      }
    });
    replayListeners();
    warnOnUnhandledEvents();
  }

  // `onAny` sees no reserved events and is not counted by `listeners()`, so neither false-positives.
  function warnOnUnhandledEvents() {
    if (!env.isLocalDev) return;

    const warned = new Set();
    socket.onAny((event) => {
      if (socket.listeners(event).length > 0 || warned.has(event)) return;
      warned.add(event);
      logger.warn(`socket event "${event}" arrived with no listener`, { event });
    });
  }

  function replayListeners() {
    for (const [event, callbacks] of listeners) {
      for (const callback of callbacks) {
        socket.on(event, callback);
      }
    }
  }

  /**
   * Registers a listener for the session. Safe before the socket exists and safe to repeat with the
   * same callback; callers never need to observe reconnects or socket replacement.
   */
  function addListener(event, callback) {
    const callbacks = listeners.get(event) ?? new Set();
    if (callbacks.has(callback)) return;

    callbacks.add(callback);
    listeners.set(event, callbacks);
    if (socket) {
      socket.on(event, callback);
    }
  }

  function removeListener(event, callback) {
    listeners.get(event)?.delete(callback);
    if (socket) {
      socket.off(event, callback);
    }
  }

  /**
   * Register a one-shot listener that auto-removes after the first invocation.
   * Returns a cleanup function that removes the listener if it hasn't fired yet.
   *
   * When a `filter` function is provided, non-matching payloads are ignored
   * and the listener stays active until a matching payload arrives or cleanup
   * is called.
   *
   * `onAbandoned` runs if the socket is torn down or replaced while the wait is still pending. The
   * awaited event cannot reach a replacement socket, so callers must settle rather than keep waiting.
   */
  function addOnceListener(event, callback, filter, onAbandoned) {
    if (!socket) return () => {};

    const wait = { onAbandoned };
    let handler;
    if (filter) {
      handler = (payload) => {
        if (!filter(payload)) return;
        pendingOnceWaits.delete(wait);
        socket?.off(event, handler);
        callback(payload);
      };
      socket.on(event, handler);
    } else {
      handler = (payload) => {
        pendingOnceWaits.delete(wait);
        callback(payload);
      };
      socket.once(event, handler);
    }
    pendingOnceWaits.add(wait);

    return () => {
      pendingOnceWaits.delete(wait);
      socket?.off(event, handler);
    };
  }

  // Impersonating keeps isLoggedIn true, yet X-On-Behalf-Of is fixed at socket construction.
  watch(
    [() => authStore.isLoggedIn, onBehalfOf],
    ([isLoggedIn]) => {
      if (!isLoggedIn) {
        disconnect();
        return;
      }
      // Shared (share-token) sessions satisfy isLoggedIn but get no live
      // updates by design — recipients refresh to see changes.
      if (tokenStore.isShareToken) {
        return;
      }
      init();
    },
    { immediate: true },
  );

  return {
    id,
    init,
    addListener,
    addOnceListener,
    removeListener,
    registerIdentity,
    productBulkImportResult,
    schedulerCallbackData,
  };
});
